import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { api, runClassifyJob, API } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AssumptionsPanel } from "@/components/classifier/AssumptionsPanel";
import { ResultView } from "@/components/classifier/ResultView";
import { Copilot } from "@/components/classifier/Copilot";
import { AiCore } from "@/components/classifier/AiCore";
import { Search, Loader2, Sparkles, MessageSquare } from "lucide-react";

const STATUS_LINES = [
  "Reading the compliance graph for your domain",
  "Identifying your role: provider versus deployer",
  "Checking Article 5 prohibited practices",
  "Matching against Annex III high-risk categories",
  "Evaluating Article 22 representative requirements",
  "Assembling your obligations and timeline",
];

const lsKey = (url, desc) => `aiact:${(url || "").trim().toLowerCase()}|${(desc || "").trim().toLowerCase()}`;

const readLS = (k) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeLS = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* best effort */
  }
};

export default function Classifier() {
  const { id: routeId } = useParams();
  const location = useLocation();

  const [input, setInput] = useState({ url: "", description: "" });
  const [answers, setAnswers] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusIdx, setStatusIdx] = useState(0);
  const [error, setError] = useState("");
  const [resultId, setResultId] = useState(routeId || "");
  const [messages, setMessages] = useState([]);
  const [copied, setCopied] = useState(false);
  const [booted, setBooted] = useState(false);
  const [changeLog, setChangeLog] = useState([]);
  const [chatOpen, setChatOpen] = useState(true);

  const saveTimer = useRef(null);
  const dirtyRef = useRef(false);

  // rotate loading status lines
  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setStatusIdx((i) => (i + 1) % STATUS_LINES.length), 1100);
    return () => clearInterval(t);
  }, [loading]);

  const runClassify = useCallback(async (url, description, ans, refresh = false) => {
    setLoading(true);
    setError("");
    setStatusIdx(0);
    try {
      const res = await runClassifyJob({ url, description, answers: ans, refresh });
      setData(res);
      return res;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "We could not complete the classification. Please try again shortly.";
      setError(typeof msg === "string" ? msg : "Something went wrong.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // --- boot: restore from route id, ?r=, ?url=, or localStorage
  useEffect(() => {
    if (booted) return;
    const params = new URLSearchParams(location.search);
    const rParam = routeId || params.get("r");
    const urlParam = params.get("url");

    const boot = async () => {
      if (rParam) {
        try {
          const { data: saved } = await api.get(`/results/${rParam}`);
          setInput(saved.input || { url: "", description: "" });
          setAnswers(saved.answers || {});
          setData(saved.classification || null);
          setMessages(saved.chat || []);
          setResultId(rParam);
          setBooted(true);
          return;
        } catch {
          /* fall through to fresh */
        }
      }
      if (urlParam) {
        const restored = readLS(lsKey(urlParam, ""));
        if (restored && restored.data) {
          setInput(restored.input || { url: urlParam, description: "" });
          setAnswers(restored.answers || {});
          setData(restored.data);
          setMessages(restored.messages || []);
          setResultId(restored.resultId || "");
          setBooted(true);
          return;
        }
        setInput({ url: urlParam, description: "" });
        await runClassify(urlParam, "", {});
      }
      setBooted(true);
    };
    boot();
  }, [booted, routeId, location.search, runClassify]);

  // --- persist refined state (debounced) whenever the user has edited something
  const persist = useCallback(
    (nextAnswers, nextData, nextMessages) => {
      writeLS(lsKey(input.url, input.description), {
        input,
        answers: nextAnswers,
        data: nextData,
        messages: nextMessages,
        resultId,
      });
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          const { data: saved } = await api.post("/results", {
            id: resultId || undefined,
            input,
            answers: nextAnswers,
            classification: nextData,
            chat: nextMessages,
          });
          if (saved.id && saved.id !== resultId) {
            setResultId(saved.id);
            window.history.replaceState(null, "", `/r/${saved.id}`);
          } else if (saved.id) {
            window.history.replaceState(null, "", `/r/${saved.id}`);
          }
        } catch {
          /* best effort */
        }
      }, 800);
    },
    [input, resultId]
  );

  // --- submit from the input form
  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!input.url && !input.description) {
      setError("Enter your company URL or describe your AI system.");
      return;
    }
    dirtyRef.current = false;
    setAnswers({});
    setMessages([]);
    setResultId("");
    setChangeLog([]);
    const res = await runClassify(input.url, input.description, {});
    if (res) window.history.replaceState(null, "", `/?url=${encodeURIComponent(input.url || "")}`);
  };

  const assumedFor = useCallback(
    (key) => {
      const a = (data?.assumptions || []).find((x) => x.key === key);
      return a ? a.assumed_value : "";
    },
    [data]
  );
  const questionFor = useCallback(
    (key) => {
      const a = (data?.assumptions || []).find((x) => x.key === key);
      return a ? a.question : key;
    },
    [data]
  );
  const currentVal = useCallback(
    (key) => (answers[key] !== undefined ? answers[key] : assumedFor(key)),
    [answers, assumedFor]
  );

  const logChange = (key, from, to, via) => {
    if (from === to) return;
    setChangeLog((prev) => [
      { key, question: questionFor(key), from, to, via, at: Date.now() },
      ...prev,
    ]);
  };

  // --- assumption edited -> live re-run
  const onAssumptionChange = async (key, value) => {
    const from = currentVal(key);
    const nextAnswers = { ...answers, [key]: value };
    setAnswers(nextAnswers);
    dirtyRef.current = true;
    logChange(key, from, value, "panel");
    const res = await runClassify(input.url, input.description, nextAnswers);
    if (res) persist(nextAnswers, res, messages);
  };

  // --- revert a single assumption back to the AI's original draft value
  const onRevertKey = async (key) => {
    const from = currentVal(key);
    const nextAnswers = { ...answers };
    delete nextAnswers[key];
    setAnswers(nextAnswers);
    dirtyRef.current = true;
    logChange(key, from, `${assumedFor(key)} (original draft)`, "revert");
    const res = await runClassify(input.url, input.description, nextAnswers);
    if (res) persist(nextAnswers, res, messages);
  };

  // --- reset every assumption to the AI's original first-pass draft
  const onResetOriginal = async () => {
    setAnswers({});
    setChangeLog([]);
    dirtyRef.current = true;
    const res = await runClassify(input.url, input.description, {});
    if (res) persist({}, res, messages);
  };

  // --- copilot changed the shared assumptions -> re-run classification live
  const onCopilotResult = async (nextAnswers, changed) => {
    Object.keys(nextAnswers).forEach((k) => {
      const from = currentVal(k);
      if (String(from) !== String(nextAnswers[k])) logChange(k, from, nextAnswers[k], "copilot");
    });
    setAnswers(nextAnswers);
    dirtyRef.current = true;
    if (changed) {
      const res = await runClassify(input.url, input.description, nextAnswers);
      if (res) persist(nextAnswers, res, messages);
    } else {
      persist(nextAnswers, data, messages);
    }
  };

  // keep chat persisted too
  const handleSetMessages = (m) => {
    setMessages(m);
    if (dirtyRef.current || data) persist(answers, data, m);
  };

  const onCopyLink = async () => {
    const link = resultId ? `${window.location.origin}/r/${resultId}` : window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch {
      /* ignore */
    } finally {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // --- dynamic document + social meta once a result exists (best-effort client update)
  useEffect(() => {
    if (!data) return;
    const meta = data.meta || {};
    const name = meta.system_name || meta.input_summary || "Your AI system";
    const summary = data.summary || "EU AI Act classification by GDPRLocal.";
    const title = `${name} AI Act classification | GDPRLocal`;
    document.title = title;
    const setTag = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setTag("property", "og:title", title);
    setTag("name", "twitter:title", title);
    setTag("property", "og:description", summary);
    setTag("name", "twitter:description", summary);
    if (resultId) {
      const img = `${API}/og/${resultId}.png`;
      setTag("property", "og:image", img);
      setTag("name", "twitter:image", img);
      setTag("property", "og:url", `${window.location.origin}/r/${resultId}`);
    }
  }, [data, resultId]);

  const hasResult = !!data;
  const showChat = hasResult && chatOpen;

  return (
    <div className="min-h-screen bg-[#F4F8FB]">
      {/* Everything except the chat shifts left when the side panel is open (lg only) */}
      <div className={showChat ? "lg:mr-[22%]" : ""}>
        <Header />

        {/* disclaimer banner */}
        <div className="border-b border-line bg-tint px-4 py-2 text-center text-xs text-muted">
          Indicative regulatory guidance based on the EU AI Act (Regulation EU 2024/1689). Not formal legal advice.
        </div>

        <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          {/* Input hero: splash text left, AI motif top-right, entry boxes below */}
          <section className="mx-auto max-w-5xl">
            <div className="grid items-center gap-6 lg:grid-cols-2">
              <div className="text-center lg:text-left">
                <span
                  data-testid="tools-eyebrow"
                  className="inline-block rounded-full bg-aqua/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-asphalt"
                >
                  GDPRLocal Tools
                </span>
                <h1 className="mt-5 text-4xl font-bold text-asphalt sm:text-5xl">EU AI Act Classifier</h1>
                <p className="mt-4 max-w-xl text-base text-muted md:text-lg">
                  Enter your company URL and, optionally, a line about your AI system. We'll run the research
                  in seconds, draft an instant classification, then you confirm and refine it. A smart draft,
                  not a form to fill.
                </p>
              </div>
              <div className="flex justify-center lg:justify-end">
                <AiCore active={loading} className="w-full max-w-sm" />
              </div>
            </div>

            <form onSubmit={onSubmit} className="mx-auto mt-6 max-w-3xl">
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                  <input
                    data-testid="input-company-url"
                    value={input.url}
                    onChange={(e) => setInput((p) => ({ ...p, url: e.target.value }))}
                    placeholder="yourcompany.com"
                    className="w-full rounded-brand border border-line py-3 pl-10 pr-3 text-sm text-asphalt outline-none focus:border-asphalt"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="button-classify-submit"
                  disabled={loading}
                  className="gl-primary-btn inline-flex items-center justify-center gap-2 px-6 py-3 text-sm disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Classify my AI system
                </button>
              </div>
              <textarea
                data-testid="input-system-description"
                value={input.description}
                onChange={(e) => setInput((p) => ({ ...p, description: e.target.value }))}
                placeholder="Describe your AI system (optional). For example: we built a tool that screens job applicants."
                rows={2}
                className="mt-3 w-full resize-none rounded-brand border border-line px-3.5 py-2.5 text-sm text-asphalt outline-none focus:border-asphalt"
              />
            </form>
            {error && (
              <p className="mt-3 text-center text-sm font-medium text-tier-prohibited" data-testid="error-message">
                {error}
              </p>
            )}
          </section>

          {/* Active Research status ticker (map runs in the hero motif above) */}
          {loading && !hasResult && (
            <div
              data-testid="loading-state"
              className="mx-auto mt-8 max-w-3xl space-y-4 rounded-xl border border-line bg-tint p-8"
            >
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-2 rounded-full bg-aqua/20 px-3 py-1 text-xs font-bold uppercase tracking-wide text-asphalt">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-aqua-dark" /> Active research
                </span>
                <span className="text-sm font-medium text-asphalt">Drafting your classification</span>
              </div>
              <ul className="space-y-2.5">
                {STATUS_LINES.map((s, i) => (
                  <li
                    key={s}
                    className={`flex items-center gap-2 text-sm transition-colors ${
                      i <= statusIdx ? "text-asphalt" : "text-muted/50"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${i <= statusIdx ? "bg-aqua-dark" : "bg-line"}`} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Results */}
          {hasResult && (
            <div className="mt-10 space-y-6">
              {loading && (
                <div
                  className="flex items-center gap-2 rounded-lg border border-line bg-tint px-4 py-2 text-sm text-asphalt"
                  data-testid="rerun-indicator"
                >
                  <Loader2 className="h-4 w-4 animate-spin" /> Re-running with your changes
                </div>
              )}

              <ResultView
                data={data}
                onCopyLink={onCopyLink}
                copied={copied}
                cacheHit={data?.meta?.cache_hit}
                generatedAt={data?.meta?.generated_at}
              />
              <AssumptionsPanel
                assumptions={data.assumptions || []}
                answers={answers}
                onChange={onAssumptionChange}
                changeLog={changeLog}
                onRevertKey={onRevertKey}
                onResetOriginal={onResetOriginal}
              />
            </div>
          )}
        </main>

        <Footer />
      </div>

      {/* Full-height right-side copilot panel (shared pattern with Rep + Policy tools) */}
      {hasResult && chatOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-asphalt/30 lg:hidden"
            onClick={() => setChatOpen(false)}
          />
          <aside
            data-testid="copilot-side-panel"
            className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-line bg-white shadow-xl sm:w-[380px] lg:w-[22%] lg:min-w-[320px]"
          >
            <Copilot
              input={input}
              answers={answers}
              classification={data}
              onResult={onCopilotResult}
              messages={messages}
              setMessages={handleSetMessages}
              onClose={() => setChatOpen(false)}
            />
          </aside>
        </>
      )}

      {/* Launcher when chat is closed */}
      {hasResult && !chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          data-testid="copilot-launcher"
          className="gl-primary-btn fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full px-5 py-3 text-sm shadow-lg"
        >
          <MessageSquare className="h-4 w-4" /> Compliance copilot
        </button>
      )}
    </div>
  );
}
